/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit';

import { sendTransactions } from '@ibm-watson/shared/modules/actions/api/transactions';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import * as modelsOperations from '@ibm-watson/shared/modules/ia/store/operations';
import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ApiStatus } from '../../../../../ts';
import type { DataType } from '../../../../../ts/action';
import type {
  CreateAsyncThunk,
  DataTypesSlice,
  Entity
} from '../../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import {
  buildOptionsList,
  DATA_TYPE_RESPONSE_TYPES,
  generateDataTypeId,
  generateEntityName,
  getValidEntityIdFromSavedResponseTitle
} from '../../../util/actionUtil';
import { fetchEntities } from '../../entities/actions';
import { entityAdapter } from '../adapter';
import { selectCustomDataTypeIds } from '../selectors';
import { listDataTypes } from './list';

interface CreatedDataType
  extends Pick<
    DataType,
    'data_type' | 'description' | 'entity' | 'num_values' | 'title'
  > {
  id?: string;
  response_type: string;
  response: {
    response_type: string;
    options: string[];
  };
}

export const createDataType = createAsyncThunk<
  CreatedDataType,
  Pick<DataType, 'description' | 'title'> & {
    responseType: string;
    values: any[];
  },
  CreateAsyncThunk
>('data-types/create', async (params, { dispatch, getState }) => {
  try {
    const {
      description,
      responseType = DATA_TYPE_RESPONSE_TYPES.option.id as 'option' | 'regex',
      title,
      values
    } = params;
    const operations = [];

    const state = getState();
    const skillId = getActionSkillId(state);

    const { entities = {} } = state.models;
    const { entities: dataTypes = {} } = state.dataTypes;
    let dataTypeId = generateDataTypeId();
    while (dataTypes && dataTypes[dataTypeId]) {
      dataTypeId = generateDataTypeId(); //ensure unique data_type id
    }
    let entityId = generateEntityName();
    while (entities && entities[entityId]) {
      entityId = generateEntityName(); //ensure unique entity id
    }
    const entityToCreate = {
      description,
      entity: entityId,
      fuzzy_match: true,
      values
    };
    operations.push({
      operation: 'create',
      payload: entityToCreate,
      target_reference: entityId,
      type: 'entity' //must create the entity first so that store maintains referential integrity
    });
    const dataTypeToCreate: CreatedDataType = {
      data_type: dataTypeId,
      description,
      entity: entityId,
      id: dataTypeId, //synthetic id that is needed for carbon data-table
      num_values: values?.length || 0,
      response_type: responseType as string,
      title,
      response: undefined
    };
    if (responseType === DATA_TYPE_RESPONSE_TYPES.option.id) {
      dataTypeToCreate.response = {
        response_type: responseType,
        options: buildOptionsList(values)
      };
    }
    operations.push({
      operation: 'create',
      payload: dataTypeToCreate,
      target_reference: dataTypeId,
      type: 'data_type'
    });

    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
    const isNewIA = selectIsNewIa(state);
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(state);
    const assistantId = getAssistantId(state);

    await sendTransactions(
      skillId,
      {
        operations
      },
      useNewIAUpdatedAccess,
      assistantId
    );
    //update 'models' with the new entity
    dispatch({
      type: '',
      payload: {
        operation: modelsOperations.CREATE,
        response: {
          entities: {
            entities: {
              [entityId]: entityToCreate
            }
          }
        }
      }
    });
    return dataTypeToCreate;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const createDataTypesFromEntities = createAsyncThunk<
  Promise<void>,
  {
    entities: Entity[];
  },
  CreateAsyncThunk
>('data-types/create-from-entities', async (params, { dispatch, getState }) => {
  try {
    const { entities = [] } = params;
    const operations = [];

    const state = getState();
    const skillId = getActionSkillId(state);

    const idSet = new Set(selectCustomDataTypeIds(state));
    entities.forEach(entity => {
      const responseType = entity.values?.some(value => value.patterns)
        ? DATA_TYPE_RESPONSE_TYPES.regex.id
        : DATA_TYPE_RESPONSE_TYPES.option.id;
      let dataTypeId = generateDataTypeId();
      while (idSet.has(dataTypeId)) {
        dataTypeId = generateDataTypeId(); //ensure unique data_type id
      }
      const dataTypeTitle = entity.entity;
      // remove the invalid ` ()` in the renamed entity
      // use `_` to replace these characters.
      entity.entity = getValidEntityIdFromSavedResponseTitle(entity.entity);
      //set fuzzy match to true
      entity.fuzzy_match = true;

      operations.push({
        operation: 'create',
        payload: entity,
        target_reference: entity.entity,
        type: 'entity' //must create the entity first so that store maintains referential integrity
      });
      const dataTypeToCreate = {
        data_type: dataTypeId,
        description: '',
        entity: entity.entity,
        id: dataTypeId, //synthetic id that is needed for carbon data-table
        num_values: entity.values?.length || 0,
        response_type: responseType,
        title: dataTypeTitle,
        response: undefined
      };
      if (responseType === DATA_TYPE_RESPONSE_TYPES.option.id) {
        dataTypeToCreate.response = {
          response_type: responseType,
          options: buildOptionsList(entity.values)
        };
      }
      operations.push({
        operation: 'create',
        payload: dataTypeToCreate,
        target_reference: dataTypeId,
        type: 'data_type'
      });
    });

    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
    const isNewIA = selectIsNewIa(state);
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(state);
    const assistantId = getAssistantId(state);

    await sendTransactions(
      skillId,
      {
        operations
      },
      useNewIAUpdatedAccess,
      assistantId
    );
    await dispatch(fetchEntities(skillId, {}, true, true));
    await dispatch(listDataTypes());
  } catch (error) {
    if (Array.isArray(error.errors)) {
      error.message = error.errors.map(({ message }) => message).join('\n');
    }
    dispatch(addErrorMessage(error));
    throw error;
  }
});

const reducer = {
  [createDataType.pending.toString()]: (state: DataTypesSlice) => {
    state.createStatus = ApiStatus.LOADING;
    state.createError = null;
  },
  [createDataType.fulfilled.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.createStatus = ApiStatus.SUCCEEDED;
    state.createError = null;
    entityAdapter.addOne(state, action.payload);
  },
  [createDataType.rejected.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.createStatus = ApiStatus.ERROR;
    state.createError = action.error;
  },
  [createDataTypesFromEntities.pending.toString()]: (state: DataTypesSlice) => {
    state.createStatus = ApiStatus.LOADING;
    state.createError = null;
  },
  [createDataTypesFromEntities.fulfilled.toString()]: (
    state: DataTypesSlice
  ) => {
    state.createStatus = ApiStatus.SUCCEEDED;
    state.createError = null;
  },
  [createDataTypesFromEntities.rejected.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.createStatus = ApiStatus.ERROR;
    state.createError = action.error;
  }
};

export default reducer;
